import {useCallback, useEffect, useRef} from 'react';

export interface VideoSeekGuardOptions {
  initialMaxSeconds?: number;
  toleranceSeconds?: number;
}

// Blocks forward seeking on an HTMLVideoElement: the frontier only advances
// through continuous playback, so any seek past it snaps back.
export function useVideoSeekGuard(
  video: HTMLVideoElement | null,
  options?: VideoSeekGuardOptions
): {getMaxPlayedSeconds: () => number} {
  const {initialMaxSeconds, toleranceSeconds = 1} = options ?? {};
  const maxPlayedSeconds = useRef(0);

  const getMaxPlayedSeconds = useCallback(() => maxPlayedSeconds.current, []);

  useEffect(() => {
    if (!video) return;

    maxPlayedSeconds.current = Math.max(
      maxPlayedSeconds.current,
      initialMaxSeconds ?? 0
    );

    const handleTimeUpdate = () => {
      const t = video.currentTime;
      const max = maxPlayedSeconds.current;
      if (t > max && t - max < 2) {
        maxPlayedSeconds.current = t;
      }
    };

    const handleSeeking = () => {
      if (video.currentTime > maxPlayedSeconds.current + toleranceSeconds) {
        video.currentTime = maxPlayedSeconds.current;
      }
    };

    const handleRateChange = () => {
      if (video.playbackRate !== 1) {
        video.playbackRate = 1;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('ratechange', handleRateChange);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('ratechange', handleRateChange);
    };
  }, [video, initialMaxSeconds, toleranceSeconds]);

  return {getMaxPlayedSeconds};
}
